
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useClientDocuments, useUploadClientDocument, useDeleteClientDocument, useDownloadClientDocument } from "@/hooks/useClientDocuments";
import { ClientDocument } from "../types";
import { Upload, Download, Trash2, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { CheckedState } from "@radix-ui/react-checkbox";

interface ClientDocumentsTabProps {
  clientId: string;
}

export const ClientDocumentsTab: React.FC<ClientDocumentsTabProps> = ({ clientId }) => {
  const { data: documents, isLoading, error } = useClientDocuments(clientId);
  const uploadMutation = useUploadClientDocument();
  const deleteMutation = useDeleteClientDocument();
  const downloadMutation = useDownloadClientDocument();

  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null as File | null,
    documentName: '',
    documentType: 'other' as ClientDocument['document_type'],
    category: '',
    expiryDate: '',
    isTemplate: false,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setUploadData(prev => ({
      ...prev,
      file,
      documentName: file ? file.name : ''
    }));
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }

    try {
      await uploadMutation.mutateAsync({
        clientId,
        file: uploadData.file,
        documentName: uploadData.documentName,
        documentType: uploadData.documentType,
        category: uploadData.category || undefined,
        expiryDate: uploadData.expiryDate || undefined,
        isTemplate: uploadData.isTemplate,
      });

      // Reset form
      setUploadData({
        file: null,
        documentName: '',
        documentType: 'other',
        category: '',
        expiryDate: '',
        isTemplate: false,
      });
      setShowUploadForm(false);
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const handleDownload = async (document: ClientDocument) => {
    try {
      await downloadMutation.mutateAsync(document);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleDelete = async (document: ClientDocument) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await deleteMutation.mutateAsync(document);
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  // Fix the CheckedState type issue
  const handleIsTemplateChange = (checked: CheckedState) => {
    setUploadData(prev => ({
      ...prev,
      isTemplate: checked === true
    }));
  };

  if (isLoading) {
    return <div>Loading documents...</div>;
  }

  if (error) {
    return <div>Error loading documents: {error.message}</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Client Documents</CardTitle>
          <Button 
            onClick={() => setShowUploadForm(!showUploadForm)}
            variant="outline"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Document
          </Button>
        </CardHeader>
        
        <CardContent>
          {showUploadForm && (
            <div className="mb-6 p-4 border rounded-lg space-y-4">
              <h3 className="text-lg font-semibold">Upload New Document</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="file">File</Label>
                  <Input
                    id="file"
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  />
                </div>
                
                <div>
                  <Label htmlFor="documentName">Document Name</Label>
                  <Input
                    id="documentName"
                    value={uploadData.documentName}
                    onChange={(e) => setUploadData(prev => ({ ...prev, documentName: e.target.value }))}
                    placeholder="Enter document name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="documentType">Document Type</Label>
                  <Select 
                    value={uploadData.documentType} 
                    onValueChange={(value: ClientDocument['document_type']) => 
                      setUploadData(prev => ({ ...prev, documentType: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agreement">Agreement</SelectItem>
                      <SelectItem value="legal">Legal</SelectItem>
                      <SelectItem value="financial">Financial</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                      <SelectItem value="communication">Communication</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="category">Category (Optional)</Label>
                  <Input
                    id="category"
                    value={uploadData.category}
                    onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="Enter category"
                  />
                </div>
                
                <div>
                  <Label htmlFor="expiryDate">Expiry Date (Optional)</Label>
                  <Input
                    id="expiryDate"
                    type="date"
                    value={uploadData.expiryDate}
                    onChange={(e) => setUploadData(prev => ({ ...prev, expiryDate: e.target.value }))}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="isTemplate" 
                    checked={uploadData.isTemplate}
                    onCheckedChange={handleIsTemplateChange}
                  />
                  <Label htmlFor="isTemplate">Is Template</Label>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleUpload}
                  disabled={uploadMutation.isPending || !uploadData.file}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowUploadForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Documents List */}
          <div className="space-y-4">
            {documents && documents.length > 0 ? (
              documents.map((document) => (
                <div key={document.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <h4 className="font-medium">{document.document_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        Type: {document.document_type} • 
                        Uploaded: {new Date(document.upload_date).toLocaleDateString()}
                        {document.category && ` • Category: ${document.category}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(document)}
                      disabled={downloadMutation.isPending}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(document)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">No documents uploaded yet.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
